import React, { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import styles from './TopCategoriesChart.module.scss';

ChartJS.register(ArcElement, Tooltip, Legend);

function formatMoney(value) {
  return new Intl.NumberFormat('ru-RU').format(Math.round(value || 0));
}

function top5PlusOther(input) {
  const arr = (input || [])
    .map((r) => ({
      name: String(r.name || '').trim(),
      value: Number(r.value) || 0,
    }))
    .filter((r) => r.name && r.value > 0)
    .sort((a, b) => b.value - a.value);

  const top = arr.slice(0, 5);
  const rest = arr.slice(5);
  const restSum = rest.reduce((acc, r) => acc + r.value, 0);
  if (restSum > 0) top.push({ name: 'Другое', value: restSum });
  return top;
}

export default function TopCategoriesChart({ className = '', monthKey }) {
  const [period, setPeriod] = useState('month'); // all | month
  const [mode, setMode] = useState('expense'); // expense | income
  const [activeIndex, setActiveIndex] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        const sp = new URLSearchParams();
        sp.set('kind', mode);
        sp.set('period', period);

        if (period === 'month') {
          if (!monthKey) {
            if (!cancelled) setRows([]);
            return;
          }
          sp.set('month', monthKey);
        }

        const res = await fetch(`/api/stats/top-categories?${sp.toString()}`);
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.error || 'Ошибка загрузки top-categories');

        const list = Array.isArray(data?.rows) ? data.rows : [];
        const normalized = top5PlusOther(list);

        if (!cancelled) setRows(normalized);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, period, monthKey]);

  useEffect(() => {
    setActiveIndex(null);
  }, [mode, period, monthKey]);

  const total = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.value) || 0), 0),
    [rows]
  );

  const labels = useMemo(() => rows.map((r) => r.name), [rows]);
  const values = useMemo(() => rows.map((r) => Number(r.value) || 0), [rows]);

  const colors = [
    '#6aa8ff',
    '#7fcf9b',
    '#f6d365',
    '#f08a8a',
    '#a78bfa',
    '#f2b27a',
  ];

  const chartData = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: values.map((_, i) => colors[i % colors.length]),
          borderColor: 'rgba(0,0,0,0)',
          borderWidth: 0,
          hoverOffset: 5,
          spacing: 0,
          cutout: '75%',
          borderRadius: 8,
        },
      ],
    };
  }, [labels, values]);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: 5 },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      onHover: (_evt, els) => {
        if (!els?.length) setActiveIndex(null);
        else setActiveIndex(els[0].index);
      },
      animation: { duration: 450 },
    };
  }, []);

  const centerLabel =
    activeIndex == null ? 'Всего' : labels[activeIndex] || 'Всего';
  const centerValue = activeIndex == null ? total : values[activeIndex] || 0;

  return (
    <div className={[styles.card, className].filter(Boolean).join(' ')}>
      <div className={styles.card__head}>
        <div className={styles.tabs}>
          <button
            type='button'
            className={[
              styles.tabs__item,
              period === 'all' ? styles.tabs__itemActive : '',
            ].join(' ')}
            onClick={() => setPeriod('all')}>
            Всё
          </button>

          <button
            type='button'
            className={[
              styles.tabs__item,
              period === 'month' ? styles.tabs__itemActive : '',
            ].join(' ')}
            onClick={() => setPeriod('month')}>
            Месяц
          </button>

          <span
            className={styles.tabs__indicator}
            style={{
              transform:
                period === 'all' ? 'translateX(0%)' : 'translateX(100%)',
            }}
            aria-hidden='true'
          />
        </div>

        <div className={styles.tabs}>
          <button
            type='button'
            className={[
              styles.tabs__item,
              mode === 'income' ? styles.tabs__itemActive : '',
            ].join(' ')}
            onClick={() => setMode('income')}>
            Доходы
          </button>

          <button
            type='button'
            className={[
              styles.tabs__item,
              mode === 'expense' ? styles.tabs__itemActive : '',
            ].join(' ')}
            onClick={() => setMode('expense')}>
            Расходы
          </button>

          <span
            className={styles.tabs__indicator}
            style={{
              transform:
                mode === 'income' ? 'translateX(0%)' : 'translateX(100%)',
            }}
            aria-hidden='true'
          />
        </div>
      </div>

      <div className={styles.card__body}>
        <div className={styles.chart}>
          <Doughnut data={chartData} options={options} />

          <div className={styles.chart__center}>
            <div className={styles.chart__centerLabel}>
              {loading ? 'Загрузка…' : centerLabel}
            </div>
            <div className={styles.chart__centerValue}>
              {formatMoney(centerValue)} ₽
            </div>
          </div>
        </div>

        <div className={styles.list}>
          {rows.map((r, idx) => (
            <button
              key={`${r.name}-${idx}`}
              type='button'
              className={[
                styles.list__row,
                activeIndex === idx ? styles.list__rowActive : '',
              ].join(' ')}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseLeave={() => setActiveIndex(null)}
              onFocus={() => setActiveIndex(idx)}
              onBlur={() => setActiveIndex(null)}>
              <div className={styles.list__left}>
                <span
                  className={styles.list__dot}
                  style={{ backgroundColor: colors[idx % colors.length] }}
                  aria-hidden='true'
                />
                <span className={styles.list__name}>{r.name}</span>
              </div>

              <div className={styles.list__right}>
                <span className={styles.list__value}>
                  {formatMoney(r.value)} ₽
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
